import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Store, Key, Eye, EyeOff, Loader2, Info, Mail, Phone, Lock, CheckCircle } from 'lucide-react';
import { getDeviceInfo, getDeviceId, getDeviceName } from '../utils/deviceInfo';
import PWAInstallIcon from '../components/PWAInstallIcon';
import LogoLoader from '../components/LogoLoader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = ({ setUser }) => {
  const navigate = useNavigate();
  
  // Login state for different methods
  const [franchiseId, setFranchiseId] = useState('');
  const [franchisePin, setFranchisePin] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('franchise-id');
  
  // First login wizard state
  const [showCredentialsWizard, setShowCredentialsWizard] = useState(false);
  const [wizardData, setWizardData] = useState(null);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardLoading, setWizardLoading] = useState(false);
  
  // Remember last used values
  useEffect(() => {
    const savedFranchiseId = localStorage.getItem('lastFranchiseId');
    const savedEmail = localStorage.getItem('lastFranchiseEmail');
    if (savedFranchiseId) setFranchiseId(savedFranchiseId);
    if (savedEmail) setEmail(savedEmail);
    setInitialLoading(false);
  }, []);

  const handleLoginSuccess = async (response) => {
    // Check if first login credentials change is required
    if (response.data.requires_password_change || response.data.requires_pin_change) {
      setWizardData({
        token: response.data.token,
        user: response.data.user,
        franchise: response.data.franchise || response.data.user?.franchise,
        requires_password_change: response.data.requires_password_change,
        requires_pin_change: response.data.requires_pin_change
      });
      setShowCredentialsWizard(true);
      return;
    }
    
    // Normal login flow
    await completeLogin(response.data);
  };

  const completeLogin = async (data) => {
    localStorage.setItem('authToken', data.token);
    
    const userData = {
      ...data.user,
      franchise: data.franchise || data.user?.franchise,
      token: data.token
    };
    
    setUser(userData);
    toast.success('Login successful!');
    
    // Save franchise data for next time
    const franchiseData = userData.franchise;
    if (franchiseData) {
      // Register session
      const deviceId = getDeviceId();
      const deviceName = getDeviceName();
      const deviceInfo = getDeviceInfo();
      
      try {
        await axios.post(`${API}/auth/check-session`, {
          franchise_id: franchiseData.id,
          device_id: deviceId,
          device_name: deviceName,
          device_info: deviceInfo
        });
      } catch (sessionError) {
        console.warn('[Login] Session registration failed:', sessionError);
      }
      
      // Save for next time
      localStorage.setItem('lastFranchiseId', franchiseData.id || '');
    }
  };

  // Login with Franchise ID + PIN
  const handleFranchiseIdLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/franchise/login-simple`, {
        franchise_id: franchiseId.trim(),
        pin: franchisePin
      });
      
      if (response.data.success) {
        localStorage.setItem('lastFranchiseId', franchiseId.trim());
        await handleLoginSuccess(response);
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorCode = error.response?.data?.error_code || error.response?.headers?.['x-error-code'];
      const errorMsg = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      
      if (errorCode === 'SUBSCRIPTION_EXPIRED') {
        toast.error('Subscription Expired! Please contact your administrator to renew.', { duration: 8000, icon: '⚠️' });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Login with Email + Password
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/franchise/login-email`, {
        email: email.trim().toLowerCase(),
        password: password
      });
      
      if (response.data.success) {
        localStorage.setItem('lastFranchiseEmail', email.trim().toLowerCase());
        await handleLoginSuccess(response);
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorCode = error.response?.data?.error_code || error.response?.headers?.['x-error-code'];
      const errorMsg = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      
      if (errorCode === 'SUBSCRIPTION_EXPIRED') {
        toast.error('Subscription Expired! Please contact your administrator to renew.', { duration: 8000, icon: '⚠️' });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Login with Phone + Password
  const handlePhoneLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/franchise/login-phone`, {
        phone: phone.trim(),
        password: password
      });
      
      if (response.data.success) {
        await handleLoginSuccess(response);
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorCode = error.response?.data?.error_code || error.response?.headers?.['x-error-code'];
      const errorMsg = error.response?.data?.detail || 'Login failed. Please check your credentials.';
      
      if (errorCode === 'SUBSCRIPTION_EXPIRED') {
        toast.error('Subscription Expired! Please contact your administrator to renew.', { duration: 8000, icon: '⚠️' });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // First login credentials wizard - update PIN
  const handleWizardPinUpdate = () => {
    if (newPin.length !== 5) {
      toast.error('PIN must be exactly 5 digits');
      return;
    }
    if (newPin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }
    setWizardStep(2);
  };

  // First login credentials wizard - update Password
  const handleWizardPasswordUpdate = async () => {
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setWizardLoading(true);
    
    try {
      await axios.post(`${API}/auth/franchise/update-credentials`, {
        new_pin: wizardData.requires_pin_change ? newPin : null,
        new_password: wizardData.requires_password_change ? newPassword : null
      }, {
        headers: { Authorization: `Bearer ${wizardData.token}` }
      });
      
      toast.success('Credentials updated successfully!');
      setShowCredentialsWizard(false);
      
      // Complete the login
      await completeLogin({
        token: wizardData.token,
        user: wizardData.user,
        franchise: wizardData.franchise
      });
    } catch (error) {
      console.error('Error updating credentials:', error);
      toast.error(error.response?.data?.detail || 'Failed to update credentials');
    } finally {
      setWizardLoading(false);
    }
  };

  // Credentials Wizard Modal
  const CredentialsWizardModal = () => {
    if (!showCredentialsWizard || !wizardData) return null;
    
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-6 w-6 text-amber-600" />
            </div>
            <CardTitle className="text-xl">First Time Login Setup</CardTitle>
            <CardDescription>
              Please set your new PIN and password for security
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className={`w-3 h-3 rounded-full ${wizardStep >= 1 ? 'bg-amber-500' : 'bg-gray-200'}`} />
              <div className={`w-8 h-0.5 ${wizardStep >= 2 ? 'bg-amber-500' : 'bg-gray-200'}`} />
              <div className={`w-3 h-3 rounded-full ${wizardStep >= 2 ? 'bg-amber-500' : 'bg-gray-200'}`} />
            </div>
            
            {wizardStep === 1 && wizardData.requires_pin_change && (
              <div className="space-y-4">
                <h3 className="font-medium text-center text-gray-700">Step 1: Set Your New PIN</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="new-pin">New 5-Digit PIN</Label>
                  <Input
                    id="new-pin"
                    type="password"
                    placeholder="Enter new PIN"
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    maxLength={5}
                    className="h-12 text-center text-2xl tracking-[0.5em]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-pin">Confirm PIN</Label>
                  <Input
                    id="confirm-pin"
                    type="password"
                    placeholder="Confirm new PIN"
                    value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    maxLength={5}
                    className="h-12 text-center text-2xl tracking-[0.5em]"
                  />
                </div>
                
                <Button
                  onClick={handleWizardPinUpdate}
                  disabled={newPin.length !== 5 || confirmPin.length !== 5}
                  className="w-full h-12 bg-amber-500 hover:bg-amber-600"
                >
                  Continue
                </Button>
              </div>
            )}
            
            {(wizardStep === 2 || !wizardData.requires_pin_change) && wizardData.requires_password_change && (
              <div className="space-y-4">
                <h3 className="font-medium text-center text-gray-700">
                  {wizardData.requires_pin_change ? 'Step 2: Set Your New Password' : 'Set Your New Password'}
                </h3>
                
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Min 8 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="h-12"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-12"
                  />
                </div>
                
                <Button
                  onClick={handleWizardPasswordUpdate}
                  disabled={wizardLoading || newPassword.length < 8}
                  className="w-full h-12 bg-amber-500 hover:bg-amber-600"
                >
                  {wizardLoading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Complete Setup
                    </span>
                  )}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-muted to-background">
      {/* Initial Loading Screen */}
      {initialLoading && (
        <div className="fixed inset-0 bg-background z-50 flex items-center justify-center">
          <LogoLoader size="lg" text="Loading..." />
        </div>
      )}
      
      {/* Login Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <LogoLoader size="md" text="Logging in..." />
        </div>
      )}
      
      {/* Credentials Wizard Modal */}
      <CredentialsWizardModal />

      {/* Main Container */}
      <div className="w-full max-w-md flex items-center justify-center">
        {/* Login Card */}
        <Card className="w-full max-w-md relative z-10 shadow-2xl border-0 flex-shrink-0" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <img src="/fos-logo.png" alt="FOS Logo" className="h-16 sm:h-20 w-auto object-contain" />
            </div>
            <CardTitle className="text-xl font-bold text-primary tracking-tight">Franchise Login</CardTitle>
            <CardDescription className="text-sm mt-1">Sign in to your franchise portal</CardDescription>
          </CardHeader>
          
          <CardContent className="pt-2">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-4">
                <TabsTrigger value="franchise-id" className="text-xs sm:text-sm px-1" data-testid="tab-franchise-id">
                  <Store className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  <span className="hidden sm:inline">ID + PIN</span>
                  <span className="sm:hidden">PIN</span>
                </TabsTrigger>
                <TabsTrigger value="email" className="text-xs sm:text-sm px-1" data-testid="tab-email">
                  <Mail className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  Email
                </TabsTrigger>
                <TabsTrigger value="phone" className="text-xs sm:text-sm px-1" data-testid="tab-phone">
                  <Phone className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  Phone
                </TabsTrigger>
              </TabsList>
              
              {/* Franchise ID + PIN Login */}
              <TabsContent value="franchise-id">
                <form onSubmit={handleFranchiseIdLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="franchise-id" className="text-sm font-medium flex items-center gap-2">
                      <Store className="h-4 w-4 text-primary" />
                      Franchise ID
                    </Label>
                    <Input
                      id="franchise-id"
                      data-testid="franchise-id-input"
                      type="text"
                      placeholder="Enter your Franchise ID"
                      value={franchiseId}
                      onChange={(e) => setFranchiseId(e.target.value)}
                      className="h-12 rounded-xl border-2 focus:border-primary font-mono"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="franchise-pin" className="text-sm font-medium flex items-center gap-2">
                      <Key className="h-4 w-4 text-primary" />
                      PIN
                    </Label>
                    <div className="relative">
                      <Input
                        id="franchise-pin"
                        data-testid="franchise-pin-input"
                        type={showPin ? 'text' : 'password'}
                        placeholder="Enter 5-digit PIN"
                        value={franchisePin}
                        onChange={(e) => setFranchisePin(e.target.value.replace(/\D/g, '').slice(0, 5))}
                        className="h-12 rounded-xl border-2 focus:border-primary pr-12"
                        maxLength="5"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPin(!showPin)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                      >
                        {showPin ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    data-testid="login-button-pin"
                    disabled={loading || !franchiseId || franchisePin.length !== 5}
                    className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full text-base font-bold uppercase tracking-wider"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Signing in...
                      </span>
                    ) : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>
              
              {/* Email + Password Login */}
              <TabsContent value="email">
                <form onSubmit={handleEmailLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-sm font-medium flex items-center gap-2">
                      <Mail className="h-4 w-4 text-primary" />
                      Email
                    </Label>
                    <Input
                      id="login-email"
                      data-testid="email-input"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 rounded-xl border-2 focus:border-primary"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email-password" className="text-sm font-medium flex items-center gap-2">
                      <Lock className="h-4 w-4 text-primary" />
                      Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="email-password"
                        data-testid="email-password-input"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-12 rounded-xl border-2 focus:border-primary pr-12"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    data-testid="login-button-email"
                    disabled={loading || !email || !password}
                    className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full text-base font-bold uppercase tracking-wider"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Signing in...
                      </span>
                    ) : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>
              
              {/* Phone + Password Login */}
              <TabsContent value="phone">
                <form onSubmit={handlePhoneLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-phone" className="text-sm font-medium flex items-center gap-2">
                      <Phone className="h-4 w-4 text-primary" />
                      Phone Number
                    </Label>
                    <Input
                      id="login-phone"
                      data-testid="phone-input"
                      type="tel"
                      placeholder="Enter your phone number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="h-12 rounded-xl border-2 focus:border-primary"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="phone-password" className="text-sm font-medium flex items-center gap-2">
                      <Lock className="h-4 w-4 text-primary" />
                      Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="phone-password"
                        data-testid="phone-password-input"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="h-12 rounded-xl border-2 focus:border-primary pr-12"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  
                  <Button
                    type="submit"
                    data-testid="login-button-phone"
                    disabled={loading || !phone || !password}
                    className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full text-base font-bold uppercase tracking-wider"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-5 w-5 animate-spin" />
                        Signing in...
                      </span>
                    ) : 'Sign In'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            
            {/* Info Box */}
            <div className="mt-6 p-3 bg-muted/50 rounded-xl flex items-start gap-3">
              <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                {activeTab === 'franchise-id' && 'Enter your Franchise ID and PIN received from your administrator.'}
                {activeTab === 'email' && 'Use your registered email and password to login.'}
                {activeTab === 'phone' && 'Use your registered phone number and password to login.'}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* PWA Install Icon - Bottom Right */}
      <PWAInstallIcon />
    </div>
  );
};

export default Login;
